<template>
  <v-app :class="theme">
    <Header
      v-if="isUserSignedIn"
      :class="theme"
      @show-keyboard-shortcuts="showKeyboardShortcuts = true"
    />

    <Navigation
      v-if="isUserSignedIn"
      :class="theme"
    />

    <v-main :class="theme">
      <PrivacyPolicy v-if="isPrivacyPolicyRoute" />

      <TermsOfService v-else-if="isTermsOfServiceRoute" />

      <Welcome v-else-if="showSignIn && isRootRoute" />

      <Auth v-else-if="showSignIn" />

      <router-view
        v-else-if="isUserSignedIn && isValidRoute"
        class="fill-height"
      />

      <v-card
        v-else-if="isUserSignedIn && !isValidRoute"
        class="ma-3"
      >
        <v-card-text>
          <span class="mr-1">404</span>
          <span>Not Found</span>
        </v-card-text>
      </v-card>

      <v-progress-circular
        v-else-if="isAuthCheckRequested || isAutoAuthing || isSigningIn"
        class="ma-3"
        indeterminate
        color="primary"
      />

      <error-card
        v-if="promptError"
        class="ma-3"
      />

      <v-snackbar
        v-model="notification"
      >
        <v-row>
          <v-col class="align-center d-flex">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="snack.message" />
          </v-col>

          <v-col class="text-right">
            <v-btn
              color="primary"
              @click="fireActionEvent(snack)"
            >
              {{ (snack.action && snack.action.name) || 'Close' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-snackbar>

      <feature-modal v-if="isUserSignedIn" />
      <settings-modal v-if="isUserSignedIn" />
      <refresh-modal v-if="isUserSignedIn" />

      <!-- Context-aware Keyboard Shortcuts Modal -->
      <v-dialog
        v-if="isUserSignedIn"
        v-model="showKeyboardShortcuts"
        max-width="600"
      >
        <v-card>
          <v-card-title class="headline primary white--text">
            <font-awesome-icon
              :icon="['fas', 'keyboard']"
              class="mr-2"
            />
            Keyboard Shortcuts » {{ currentPageTitle }}
          </v-card-title>

          <v-card-text class="pt-4">
            <div v-if="currentRoute.name === 'Time'">
              <!-- Time Page Shortcuts -->
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        N
                      </v-chip>
                      New Time Entry
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        Ctrl/Cmd + Click
                      </v-chip>
                      Select Multiple Entries
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        /
                      </v-chip>
                      Focus Search
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        Esc
                      </v-chip>
                      Close Dialogs / Clear Selection
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <div v-else>
              <!-- Default/General Shortcuts -->
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        ?
                      </v-chip>
                      Show Keyboard Shortcuts
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        /
                      </v-chip>
                      Focus Search
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        Esc
                      </v-chip>
                      Close Dialogs
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              text
              @click="showKeyboardShortcuts = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import PATHS from 'src/router/paths'

import { fireanalytics } from 'src/store/firebase'

const {
  mapGetters: mapAuthGetters,
  mapState: mapAuthState,
} = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Layout',
  components: {
    Auth: () => import('src/auth/views/Auth'),
    Welcome: () => import('src/auth/views/Welcome'),
    PrivacyPolicy: () => import('src/docs/views/PrivacyPolicy'),
    TermsOfService: () => import('src/docs/views/TermsOfService'),
    ErrorCard: () => import('../components/ErrorCard'),
    Header: () => import('../components/Header'),
    Navigation: () => import('../components/Navigation'),
    FeatureModal: () => import('../components/FeatureModal'),
    RefreshModal: () => import('../components/RefreshModal'),
    SettingsModal: () => import('../components/SettingsModal'),
  },
  data() {
    return {
      showKeyboardShortcuts: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
      'isActiveUser',
      'isUserSignedIn',
      'showSignIn',
    ]),
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      isAutoAuthing: (state) => state.isAutoAuthing,
      isAuthCheckRequested: (state) => state.isAuthCheckRequested,
      isSigningIn: (state) => state.isSigningIn,
      user: (state) => state.user,
    }),
    ...mapLayoutState({
      currentRoute: (state) => state.currentRoute,
      darkMode: (state) => state.darkMode,
      promptError: (state) => state.promptError,
    }),
    ...mapToolsGetters(['isValidRoute', 'organizations', 'reportKey']),
    ...mapToolsState({
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      snack: (state) => state.snack,
      snackbar: (state) => state.snackbar,
      subId: (state) => state.subId,
    }),
    currentPageTitle() {
      return this.reportName || 'General'
    },
    notification: {
      get() {
        return this.snackbar
      },
      set(value) {
        this.$store.commit('tools/snackbar', value, { root: true })
      },
    },
    customReportOptions() {
      return this.searchResults.endpoints['custom-reports']?.items?.list || []
    },
    theme() {
      return this.inDarkMode ? 'black white--text' : 'icewhite black--text'
    },
    isPrivacyPolicyRoute() {
      // Reachable with or without a signed-in session - the OAuth consent
      // screen's registered privacy policy URL must work for signed-out
      // visitors (including Google's own review crawler), and existing
      // signed-in users linking to it shouldn't hit a 404 either.
      return this.$route.name === 'privacy-policy'
    },
    isTermsOfServiceRoute() {
      // Same reasoning as isPrivacyPolicyRoute. TermsOfService.vue itself
      // carries a prominent "DRAFT - Pending Legal Review" banner, so
      // being reachable here doesn't present it as approved or binding.
      return this.$route.name === 'terms-of-service'
    },
    isRootRoute() {
      // Only the bare "/" home path shows the informational Welcome view
      // for signed-out visitors - deep links (e.g. /sign-in, /admin/users,
      // a password reset link) should still go straight to the relevant
      // Auth flow, not an intermediate landing page.
      return this.$route.name === 'dashboard'
    },
  },
  watch: {
    async $route(to, from) {
      // @todo Restore document title setter.
      this.setTitle(to, from || {})
    },
    inDarkMode(newTheme) {
      this.updateTheme(newTheme ? 'dark' : 'light')
    },
    isUserSignedIn(newValue) {
      // Use actual route params instead of tools store state to avoid timing issues
      this.setTitle(this.$route, this.$route)

      // Pre-auth-only routes (the sign-in form, registration, password
      // reset/forgot flows, the raw /index.html entry) aren't real in-app
      // destinations - isValidRoute won't recognize their route names, so
      // completing sign-in while sitting on one leaves the user stranded
      // on a "404 Not Found" card instead of landing on their dashboard.
      // This was never an issue before /sign-in existed as its own route,
      // since the sign-in form always used to render at "/" itself.
      const preAuthOnlyRouteNames = ['sign-in', 'sign-up', 'forgot-password', 'reset-password', 'index']
      if (newValue && preAuthOnlyRouteNames.includes(this.$route.name)) {
        this.$router.push('/')
      }
    },
  },
  methods: {
    updateTheme(newTheme) {
      this.$store.dispatch('layout/setDarkMode', newTheme === 'dark')
      this.$vuetify.theme.dark = newTheme === 'dark'
    },
    fireActionEvent(event) {
      if (event && event.action) {
        this.$store.dispatch(event.action)
      } else {
        this.notification = false
      }
    },
    setTitle(to, from) {
      const titleMap = {
        dashboard: () => 'Dashboard',
        docs: () => 'Docs',
        home: () => 'Home',

        [PATHS.REPORTS.DISCREPANCY]: (itemLabel) => `${itemLabel || 'Default'} | Discrepancy`,
        [PATHS.REPORTS.PACING]: (itemLabel) => `${itemLabel || 'Default'} | Pacing`,
        [PATHS.REPORTS.ADX_HEALTH]: (itemLabel) => `${itemLabel || 'Default'} | AdX Health`,
        [PATHS.REPORTS.NETWORK_HEALTH]: (itemLabel) => `${itemLabel || 'Default'} | Network Health`,
        [PATHS.REPORTS.YIELD_PARTNER]: (itemLabel) => `${itemLabel || 'Default'} | AdX & OB`,

        time: (itemLabel) => `${itemLabel || 'All'} | Time`,

        organization: (itemLabel) => `${itemLabel || 'All'} | Org`,
        person: (itemLabel) => `${itemLabel || 'All'} | Person`,
        project: (itemLabel) => `${itemLabel || 'All'} | Project`,
      }

      const { isUserSignedIn, reportKey } = this
      const toParams = to?.params || this
      const fromParams = from?.params || this
      const setReportId = (rid, sid) => (sid && sid !== '-1' && `${rid}/${sid}`) || `${rid}`

      const { reportId: toReportId, reportName: toReportName, reportPath: toReportPath, subId: toSubId } = toParams
      const toIdentifier = (toReportId && setReportId(toReportId, toSubId)) || ''
      const getTitle = (toId) => {
        // Use toReportName as fallback since reportKey depends on Vuex store state.route,
        // which may not be synced yet when called from isUserSignedIn watcher
        const titleKey = reportKey || toReportName || 'dashboard'
        const foundOrg = this.organizations.find((org) => org.reportIds.indexOf(toReportId) > -1)
        const identifierForPageTitle = (foundOrg && foundOrg.orgName) || toId
        const isCustomReportRoute = this.$route.name === 'custom-reports'
        const findCustomReportConfig = () => {
          const findCustomReport = () => {
            const customReportPermissions = foundOrg && foundOrg.access?.permissions?.customReports
            return Array.isArray(customReportPermissions) && customReportPermissions.find((customReport) => customReport.href === toReportName)
          }
          return isCustomReportRoute && findCustomReport()
        }
        const getCustomReportTitle = (customReport) => {
          const getPageTitleForList = () => !customReport && 'All | Custom Report'
          const getPageTitleForReport = () => customReport && customReport.name && `${customReport.name} | Custom Report`
          return isCustomReportRoute && (getPageTitleForList() || getPageTitleForReport() || 'Unknown | Custom Report')
        }
        const makePageTitle = (pageId) => typeof titleMap[titleKey] === 'function' && titleMap[titleKey](pageId)
        return makePageTitle(identifierForPageTitle) || getCustomReportTitle(findCustomReportConfig()) || '404 Not Found'
      }
      // Signed-out visitors to the bare root path see the Welcome view, not
      // the Sign In form - title it as the app name, matching the OAuth
      // consent screen's configured app name, rather than "Sign In".
      const isSignedOutOnRootRoute = !isUserSignedIn && to?.name === 'dashboard'
      // Privacy Policy / Terms of Service are reachable regardless of auth
      // state (see isPrivacyPolicyRoute/isTermsOfServiceRoute) - title them
      // from their own route meta rather than falling through to "Sign In".
      const isPublicLegalRoute = ['privacy-policy', 'terms-of-service'].includes(to?.name)
      const page_title = (isUserSignedIn && getTitle(toIdentifier))
        || (isPublicLegalRoute && to.meta.title)
        || (isSignedOutOnRootRoute && 'iAdOps')
        || 'Sign In'

      document.title = page_title

      const { location: { href: page_location, pathname: page_path } } = document
      const { reportId: fromReportId, reportName: fromReportName, reportPath: fromReportPath, subId: fromSubId } = fromParams
      const fromIdentifier = (fromReportId && setReportId(fromReportId, fromSubId)) || ''

      const fromPath = `${fromReportName || ''}/${(fromReportName && fromReportPath && `${fromReportPath}/`) || ''}${fromIdentifier}`
      const toPath = `${toReportName || ''}/${(toReportName && toReportPath && `${toReportPath}/`) || ''}${toIdentifier}`

      fireanalytics.event('page_view', {
        page_location,
        page_path,
        page_title,
      })

      fireanalytics.event('navigation', {
        category: 'Navigation',
        description: 'Update Route',
        label: `Update Route from "${fromPath}" to "${toPath}"`,
        pageLocation: page_location,
        pagePath: page_path,
        pageTitle: page_title,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

main {
  background-color: $color-icewhite;
}
</style>
